import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import BoardEditor from '../../../Components/BoardEditor';
import {
  useChapterChat,
  useCourseEditor,
  usePageHistory,
  EditOp,
} from '../../../Hooks/LmsCourseApi/useCourseEditor';
import {
  errorText,
  useCourseReview,
  useReviewActions,
} from '../../../Hooks/LmsCourseApi/useCourseReview';
import {useGeneratedCourse} from '../../../Hooks/LmsCourseApi/useGeneratedCourse';
import {
  initialPuzzleRun,
  PuzzleAction,
  puzzleRunReducer,
  PuzzleRun,
} from '../courseLogic';
import {contentWidth, S, STATUS, T} from '../courseTheme';
import {chapterStatus, ReviewPage} from '../reviewLogic';
import ChapterPane from './ChapterPane';
import ChapterRail from './ChapterRail';
import ChatPanel, {SendHandlers} from './ChatPanel';
import {
  ChangedField,
  changedFields,
  chatContext,
  chatErrorText,
  failingChecks,
  isUndoShortcut,
  latestUndoableBatch,
  railRows,
  reviewProgress,
  runForPage,
  suggestedPrompts,
  trimContext,
  upNext,
} from './editorLogic';
import EditorHeader from './EditorHeader';
import {
  CHAT_WIDTH,
  E,
  EDITOR_BREAKPOINT,
  HEADER_HEIGHT,
  RAIL_WIDTH,
} from './editorTheme';
import {Button, ring, Sheet, useReducedMotion, focusVisible} from './ui';
import RenameSheet from './RenameSheet';

type Props = {bookUuid: string; onBack: () => void};

/** The rail starts open only when the chapter pane would still have room. */
const RAIL_OPEN_FROM = 1240;
const REPLY_DELAY_MS = 700;
/** How far from a chapter a discarded candidate may be and still be "nearby". */
const NEARBY_SEC = 120;

// ---- the play-through: the shared puzzle reducer, plus a reset per page ----------

type RunState = {key: string; run: PuzzleRun};
type RunAction = PuzzleAction | {type: 'reset'; key: string; run: PuzzleRun};

function runReducer(state: RunState, action: RunAction): RunState {
  if (action.type === 'reset') {
    // Bump the board key so chessboard-package, which keeps its own copy of
    // the position, is remounted on the new one.
    return {key: action.key, run: {...action.run, board: state.run.board + 1}};
  }
  return {key: state.key, run: puzzleRunReducer(state.run, action)};
}

const runKeyOf = (page: ReviewPage) =>
  [
    page.id,
    page.content,
    JSON.stringify(page.config?.review?.lines?.map(l => l.uci) ?? null),
    JSON.stringify((page as any).moves ?? null),
  ].join('|');

// ---- "opened": a reviewer's own record, kept per course --------------------------

function readOpened(key: string): Set<number> {
  try {
    const raw = (globalThis as any).localStorage?.getItem(key);
    const ids = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(ids)
        ? ids.filter((n: unknown) => typeof n === 'number')
        : [],
    );
  } catch {
    return new Set();
  }
}

function chapterTime(page: ReviewPage): number | null {
  const c: any = page.config ?? {};
  if (typeof c.source_timestamp === 'number') return c.source_timestamp;
  if (typeof c.pose_end === 'number') return c.pose_end;
  if (c.video_segment && typeof c.video_segment.start === 'number')
    return (c.video_segment.start + c.video_segment.end) / 2;
  return null;
}

function Tabs({
  tab,
  onTab,
}: {
  tab: 'chapter' | 'chat';
  onTab: (t: 'chapter' | 'chat') => void;
}) {
  return (
    <View
      accessibilityRole="tablist"
      style={{
        flexDirection: 'row',
        backgroundColor: E.surface,
        borderBottomWidth: 1,
        borderBottomColor: E.line,
      }}>
      {(['chapter', 'chat'] as const).map(t => (
        <TabButton
          key={t}
          label={t === 'chapter' ? 'Chapter' : 'Chat'}
          selected={tab === t}
          onPress={() => onTab(t)}
        />
      ))}
    </View>
  );
}

function TabButton({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityLabel={label}
      accessibilityState={{selected}}
      onPress={onPress}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={({pressed}) => [
        {
          flex: 1,
          minHeight: 48,
          alignItems: 'center',
          justifyContent: 'center',
          borderBottomWidth: 3,
          borderBottomColor: selected ? E.accent : 'transparent',
          backgroundColor: pressed ? E.pressed : 'transparent',
          cursor: 'pointer',
        } as any,
        ring(focused, true),
      ]}>
      <CCText
        style={{
          fontSize: T.base,
          fontWeight: selected ? '700' : '500',
          color: selected ? E.ink : E.muted,
        }}>
        {label}
      </CCText>
    </Pressable>
  );
}

/**
 * Review and fix a generated course by talking to it.
 *
 * Chat-first: the reviewer says what is wrong ("Black is to move", "the
 * answer is Rxh7+", "there's a puzzle at 31:12 it missed"), the server turns
 * that into validated edits and applies them at once, and every change can be
 * undone. The screen keeps out of the way — a rail of chapters, the chapter
 * itself (board or video), and the chat. Everything else is collapsed into
 * Details or a ⋯ menu.
 *
 * Replaces ReviewScreen on the same route (the `review` view of the Courses tab).
 */
export default function EditorScreen({bookUuid, onBack}: Props) {
  const {width, height} = useWindowDimensions();
  const narrow = width < EDITOR_BREAKPOINT;
  const reduced = useReducedMotion();

  // "Opened" is a fact about a person having looked, and the publish gate
  // demands it for every puzzle. Kept per course so a refresh does not lose it.
  const storeKey = `ccReviewOpened:${bookUuid}`;
  const [opened, setOpened] = useState<Set<number>>(() => readOpened(storeKey));
  useEffect(() => {
    try {
      (globalThis as any).localStorage?.setItem(
        storeKey,
        JSON.stringify(Array.from(opened)),
      );
    } catch {
      /* private mode or quota — the gate just asks again */
    }
  }, [opened, storeKey]);
  const openedList = useMemo(
    () => Array.from(opened).sort((a, b) => a - b),
    [opened],
  );

  const review = useCourseReview(bookUuid, openedList);
  const actions = useReviewActions(bookUuid);
  const editor = useCourseEditor(bookUuid);

  const data = review.data;
  const chapters = useMemo(() => data?.chapters ?? [], [data]);
  const pages = useMemo(() => chapters.flatMap(c => c.pages), [chapters]);
  const flags = useMemo(() => data?.flags ?? [], [data]);

  const [currentId, setCurrentId] = useState<number | null>(null);
  const [railOpen, setRailOpen] = useState(
    () => contentWidth(width) >= RAIL_OPEN_FROM,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tab, setTab] = useState<'chapter' | 'chat'>('chapter');
  const [needsOnly, setNeedsOnly] = useState(false);
  const [engineOn, setEngineOn] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [popover, setPopover] = useState(false);
  const [sheet, setSheet] = useState<null | 'history' | 'flag' | 'board' | 'rename-course' | 'rename-chapter'>(null);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [boardNotice, setBoardNotice] = useState<string | null>(null);
  const [chatNotice, setChatNotice] = useState<string | null>(null);
  const [playhead, setPlayhead] = useState<number | null>(null);
  const [flash, setFlash] = useState<ChangedField[]>([]);
  const [flagNote, setFlagNote] = useState('');

  const openPage = useCallback((id: number) => {
    setCurrentId(id);
    setOpened(s => (s.has(id) ? s : new Set(s).add(id)));
    setBoardNotice(null);
    setChatNotice(null);
    setPlayhead(null);
    setPopover(false);
    setDrawerOpen(false);
    setTab('chapter');
  }, []);

  // Open where the pipeline was least sure, not on chapter 1. And if the
  // chapter on screen goes away (an undo removed it), land somewhere real.
  useEffect(() => {
    if (!pages.length) return;
    if (currentId != null && pages.some(p => p.id === currentId)) return;
    const first =
      pages.find(p =>
        ['bad', 'warn'].includes(chapterStatus(p, flags).level),
      ) ??
      pages.find(
        p => p.content_type === 'chess_position' && !opened.has(p.id),
      ) ??
      pages[0];
    openPage(first.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, flags, currentId, openPage]);

  const current = pages.find(p => p.id === currentId) ?? null;
  const chapter =
    chapters.find(c => c.pages.some(p => p.id === currentId)) ?? null;
  const isPuzzle = current?.content_type === 'chess_position';

  // ---- play mode ------------------------------------------------------------------
  const [runState, dispatchRun] = useReducer(runReducer, undefined, () => ({
    key: '',
    run: initialPuzzleRun('', null),
  }));
  const runKey = current && isPuzzle ? runKeyOf(current) : '';
  if (current && isPuzzle && runState.key !== runKey) {
    // A different chapter, or an edit changed its position or lines: start
    // over on what is there now. Done during render so no frame shows the old run.
    dispatchRun({type: 'reset', key: runKey, run: runForPage(current)});
  }
  const run = runState.run;
  const dispatch = useCallback((a: PuzzleAction) => dispatchRun(a), []);
  useEffect(() => {
    if (run.phase !== 'replying') return undefined;
    const id = setTimeout(
      () => dispatchRun({type: 'replyLanded'}),
      REPLY_DELAY_MS,
    );
    return () => clearTimeout(id);
  }, [run.phase, run.reply]);

  // ---- an applied edit: highlight what changed for a second --------------------------
  const prevPage = useRef<ReviewPage | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const prev = prevPage.current;
    prevPage.current = current;
    if (!prev || !current || prev === current) return;
    if (prev.id !== current.id) {
      setFlash([]);
      return;
    }
    const changed = changedFields(prev, current);
    if (!changed.length) return;
    setFlash(changed);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash([]), 1000);
  }, [current]);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );

  // ---- chat and history --------------------------------------------------------------
  const chat = useChapterChat(bookUuid, currentId);
  const history = usePageHistory(bookUuid, currentId);
  const latestUndoable = latestUndoableBatch(history.data);
  const sendVars = (editor.send as any).variables as
    | {pageId?: number}
    | undefined;
  const sending =
    !!editor.send.isLoading &&
    (sendVars?.pageId == null || sendVars.pageId === currentId);

  const send = (message: string, handlers: SendHandlers) => {
    if (!current) return;
    setChatNotice(null);
    editor.send.mutate(
      {
        pageId: current.id,
        message,
        context: chatContext({
          run: isPuzzle ? run : null,
          videoTime: isPuzzle ? null : playhead,
        }),
      },
      {onError: handlers.onError},
    );
  };

  const undo = (batchId?: string) => {
    if (!current) return;
    setChatNotice(null);
    editor.undo.mutate(
      batchId ? {pageId: current.id, batchId} : {pageId: current.id},
      {
        onError: (e: unknown) => setChatNotice(chatErrorText(e)),
      },
    );
  };

  const edit = (ops: EditOp[], after?: {onSuccess?: () => void}) => {
    if (!current) return;
    setBoardNotice(null);
    editor.edit.mutate(
      {pageId: current.id, ops},
      {
        onSuccess: () => after?.onSuccess?.(),
        onError: (e: unknown) => setBoardNotice(chatErrorText(e)),
      },
    );
  };

  // Cmd/Ctrl+Z undoes the page's latest edit — unless a text field has focus,
  // where it is that field's own undo.
  const undoRef = useRef(undo);
  undoRef.current = undo;
  useEffect(() => {
    const doc = (globalThis as any).document;
    if (!doc?.addEventListener) return undefined;
    const handler = (e: any) => {
      if (!isUndoShortcut(e, doc.activeElement)) return;
      e.preventDefault?.();
      undoRef.current();
    };
    doc.addEventListener('keydown', handler);
    return () => doc.removeEventListener('keydown', handler);
  }, []);

  // ---- the video's signed URL (the review payload carries the raw key) ----------------
  const generated = useGeneratedCourse(
    current && !isPuzzle ? bookUuid : undefined,
  );
  // Kept per recording once signed. Every edit marks the student copy stale,
  // and each re-read signs the URL afresh — a new string that would reload the
  // video under the reviewer. A chapter an edit just created is not in that
  // copy at all; it plays the same file as its neighbours.
  const signedByFile = useRef<Record<string, string>>({});
  const videoSrc = useMemo(() => {
    if (!current || isPuzzle) return null;
    const file = String(current.config?.video_course_id ?? '');
    if (file && signedByFile.current[file]) return signedByFile.current[file];
    const videos = (generated.data?.chapters ?? [])
      .flatMap((c: any) => c.pages ?? [])
      .filter((p: any) => /^https?:\/\//.test(p.content ?? ''));
    const signed = (
      videos.find((p: any) => p.id === current.id) ??
      videos.find(
        (p: any) => file && String(p.config?.video_course_id ?? '') === file,
      )
    )?.content;
    if (signed) {
      if (file) signedByFile.current[file] = signed;
      return signed as string;
    }
    return /^https?:\/\//.test(current.content) ? current.content : null;
  }, [current, isPuzzle, generated.data]);

  // ---- render -----------------------------------------------------------------------
  if (review.isLoading || !data) {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: E.canvas,
          alignItems: 'center',
          justifyContent: 'center',
          gap: S[3],
        }}>
        {review.error ? (
          <>
            <CCText style={{fontSize: T.base, color: E.ink}}>
              The course could not be loaded.
            </CCText>
            <Button label="Try again" onPress={() => review.refetch?.()} />
          </>
        ) : (
          <ActivityIndicator
            color={E.accent}
            accessibilityLabel="Loading the course"
          />
        )}
      </View>
    );
  }

  const allRows = railRows(chapters, flags, opened, false);
  const rows = needsOnly ? allRows.filter(r => r.status.needsReview) : allRows;
  const progress = reviewProgress(pages, opened);
  const nextUp = current ? upNext(pages, flags, current.id, opened) : null;
  const numberOf = (pageId: number) =>
    chapters.find(c => c.pages.some(p => p.id === pageId))?.number ?? 0;
  const failing = failingChecks(data.gate, chapters);

  const publish = () => {
    if (!data.gate.ok) {
      setPopover(o => !o);
      return;
    }
    publishNow();
  };
  // The checks advise; a reviewer who has looked can publish whatever they say.
  const publishNow = () => {
    setPopover(false);
    actions.publish.mutate(
      {opened: openedList},
      {
        onError: (e: unknown) =>
          setBoardNotice(errorText(e, 'Not ready to publish.')),
      },
    );
  };

  const t = current ? chapterTime(current) : null;
  const discarded = (data.discarded ?? [])
    .map((item, index) => ({index, item}))
    .filter(
      ({item}) =>
        t == null || item.time == null || Math.abs(item.time - t) <= NEARBY_SEC,
    );

  const paneEstimate = {
    width: narrow
      ? width
      : contentWidth(width) - (railOpen ? RAIL_WIDTH : 0) - CHAT_WIDTH,
    height: height - HEADER_HEIGHT - (narrow ? 48 : 0),
  };

  const rail = (closeLabel: string, onClose: () => void) => (
    <ChapterRail
      rows={rows}
      total={allRows.length}
      needsReviewCount={allRows.filter(r => r.status.needsReview).length}
      currentPageId={currentId}
      needsReviewOnly={needsOnly}
      onToggleFilter={() => setNeedsOnly(v => !v)}
      onOpen={openPage}
      onClose={onClose}
      closeLabel={closeLabel}
      reduced={reduced}
    />
  );

  const pane =
    current && chapter ? (
      <ChapterPane
        chapter={chapter}
        page={current}
        flags={flags}
        discarded={discarded}
        run={run}
        dispatch={dispatch}
        engineOn={engineOn}
        onEngine={setEngineOn}
        detailsOpen={detailsOpen}
        onDetails={setDetailsOpen}
        flash={flash}
        reduced={reduced}
        notice={boardNotice}
        estimate={paneEstimate}
        videoSrc={videoSrc}
        videoLoading={!!generated.isLoading}
        playhead={playhead}
        onTime={s =>
          setPlayhead(prev =>
            prev != null && Math.floor(prev) === Math.floor(s) ? prev : s,
          )
        }
        onRename={() => {
          setRenameError(null);
          setSheet('rename-chapter');
        }}
        onSetTimes={(start, end, after) =>
          edit([{op: 'set_times', start, end}], after)
        }
        savingTimes={!!editor.edit.isLoading}
        timeContext={isPuzzle ? null : trimContext(chapters, current.id)}
        onAcceptAlternative={san => edit([{op: 'add_alternative', san}])}
        onKeepTitle={() =>
          edit([{op: 'set_title', text: current.heading ?? chapter.title}])
        }
        onDismiss={(flagId, reason) =>
          actions.dismiss.mutate(
            {flagId, reason},
            {
              onError: (e: unknown) =>
                setBoardNotice(errorText(e, 'Could not mark it fine.')),
            },
          )
        }
        onRestore={index => edit([{op: 'restore_discarded', index}])}
        onEditPosition={() => setSheet('board')}
        onHistory={() => setSheet('history')}
        onFlag={() => {
          setFlagNote('');
          setSheet('flag');
        }}
      />
    ) : (
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: S[6],
        }}>
        <CCText style={{fontSize: T.base, color: E.muted}}>
          This course has no chapters yet.
        </CCText>
      </View>
    );

  const chatPanel = current ? (
    <View style={{flex: 1, minHeight: 0}}>
      {chatNotice ? (
        <View style={{paddingHorizontal: S[4], paddingTop: S[3]}}>
          <View
            accessibilityLiveRegion="polite"
            style={{flexDirection: 'row', gap: S[2], alignItems: 'flex-start'}}>
            <Ionicons
              name="alert-circle-outline"
              size={18}
              color={STATUS.warn.fg}
            />
            <CCText
              style={{flex: 1, fontSize: T.sm, color: E.ink, lineHeight: 19}}>
              {chatNotice}
            </CCText>
          </View>
        </View>
      ) : null}
      <ChatPanel
        key={current.id}
        chapterLabel={`Chapter ${chapter?.number ?? ''}`}
        messages={chat.data ?? []}
        loading={!!chat.isLoading}
        sending={sending}
        suggestions={suggestedPrompts(current, flags)}
        history={history.data}
        latestUndoable={latestUndoable}
        onSend={send}
        onUndo={batchId => undo(batchId)}
        reduced={reduced}
      />
    </View>
  ) : null;

  const revisions = history.data ?? [];

  return (
    <View style={{flex: 1, backgroundColor: E.canvas}}>
      <EditorHeader
        title={data.book.title}
        reviewState={data.review_state}
        progress={progress}
        next={nextUp ? {...nextUp, number: numberOf(nextUp.id)} : null}
        onNext={() => nextUp && openPage(nextUp.id)}
        onBack={onBack}
        onRename={() => {
          setRenameError(null);
          setSheet('rename-course');
        }}
        showRailToggle={narrow || !railOpen}
        onShowRail={() => (narrow ? setDrawerOpen(true) : setRailOpen(true))}
        narrow={narrow}
        gateOk={!!data.gate.ok}
        failing={failing}
        publishing={!!actions.publish.isLoading}
        onPublish={publish}
        onPublishAnyway={publishNow}
        onUnpublish={() =>
          actions.unpublish.mutate(undefined, {
            onError: (e: unknown) =>
              setBoardNotice(errorText(e, 'Could not unpublish.')),
          })
        }
        popoverOpen={popover}
        onClosePopover={() => setPopover(false)}
        onJump={openPage}
      />

      {narrow ? (
        <>
          <Tabs tab={tab} onTab={setTab} />
          <View
            style={{
              flex: 1,
              minHeight: 0,
              backgroundColor: tab === 'chat' ? E.surface : E.canvas,
            }}>
            {tab === 'chapter' ? pane : chatPanel}
          </View>
        </>
      ) : (
        <View style={{flex: 1, minHeight: 0, flexDirection: 'row'}}>
          {railOpen ? (
            <View
              style={{
                width: RAIL_WIDTH,
                borderRightWidth: 1,
                borderRightColor: E.line,
              }}>
              {rail('Hide chapters', () => setRailOpen(false))}
            </View>
          ) : null}
          <View style={{flex: 1, minWidth: 0}}>{pane}</View>
          <View
            style={{
              width: CHAT_WIDTH,
              borderLeftWidth: 1,
              borderLeftColor: E.line,
            }}>
            {chatPanel}
          </View>
        </View>
      )}

      {narrow && drawerOpen ? (
        <View
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 45,
            flexDirection: 'row',
          }}>
          <View
            accessibilityViewIsModal
            style={{
              width: Math.min(320, Math.round(width * 0.86)),
              backgroundColor: E.surface,
              shadowColor: '#000',
              shadowOpacity: 0.2,
              shadowRadius: 24,
              elevation: 12,
            }}>
            {rail('Close chapters', () => setDrawerOpen(false))}
          </View>
          <Pressable
            accessible={false}
            focusable={false}
            onPress={() => setDrawerOpen(false)}
            style={{flex: 1, backgroundColor: E.scrim}}
          />
        </View>
      ) : null}

      {sheet === 'rename-course' ? (
        <RenameSheet
          title="Rename course"
          value={data.book.title ?? ''}
          saving={!!actions.editCourse.isLoading}
          error={renameError}
          onClose={() => setSheet(null)}
          onSave={name =>
            actions.editCourse.mutate(
              {title: name},
              {
                onSuccess: () => setSheet(null),
                onError: (e: unknown) =>
                  setRenameError(errorText(e, 'Could not rename the course.')),
              },
            )
          }
        />
      ) : null}

      {sheet === 'rename-chapter' && current ? (
        <RenameSheet
          title="Rename chapter"
          value={chapter?.title || current.heading || ''}
          saving={!!editor.edit.isLoading}
          error={renameError}
          onClose={() => setSheet(null)}
          onSave={name =>
            editor.edit.mutate(
              {pageId: current.id, ops: [{op: 'set_title', text: name}]},
              {
                onSuccess: () => setSheet(null),
                onError: (e: unknown) => setRenameError(chatErrorText(e)),
              },
            )
          }
        />
      ) : null}

      {sheet === 'board' && current && isPuzzle ? (
        <Sheet title="Edit position" width={720} onClose={() => setSheet(null)}>
          <ScrollView>
            <BoardEditor
              initialFen={current.content}
              minFontSize={12}
              onApply={fen => {
                setSheet(null);
                edit([{op: 'set_fen', fen}]);
              }}
              onClose={() => setSheet(null)}
            />
          </ScrollView>
        </Sheet>
      ) : null}

      {sheet === 'history' && current ? (
        <Sheet title="History" onClose={() => setSheet(null)}>
          <ScrollView contentContainerStyle={{padding: S[5], gap: S[1]}}>
            {revisions.length ? (
              revisions.map((r, i) => {
                const live = r.batch_id === latestUndoable;
                return (
                  <View
                    key={r.batch_id}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: S[3],
                      paddingVertical: S[2],
                      borderTopWidth: i ? 1 : 0,
                      borderTopColor: E.line,
                    }}>
                    <Ionicons
                      name={
                        r.source === 'chat'
                          ? 'chatbubble-outline'
                          : 'create-outline'
                      }
                      size={18}
                      color={E.muted}
                    />
                    <View style={{flex: 1, gap: 2}}>
                      <CCText
                        style={{
                          fontSize: T.sm + 1,
                          color: r.undone_at ? E.muted : E.ink,
                          lineHeight: 20,
                        }}>
                        {r.labels.length ? r.labels.join(' · ') : 'Edit'}
                      </CCText>
                      <CCText style={{fontSize: T.xs, color: E.muted}}>
                        {[
                          r.source === 'chat'
                            ? 'From the chat'
                            : 'From the editor',
                          new Date(r.created_at).toLocaleString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            day: 'numeric',
                            month: 'short',
                          }),
                          r.undone_at ? 'Undone' : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </CCText>
                    </View>
                    {r.undone_at ? null : (
                      <Button
                        label="Undo"
                        icon="arrow-undo"
                        variant="quiet"
                        accessibilityLabel={`Undo ${
                          r.labels.join(', ') || 'this edit'
                        }`}
                        accessibilityHint={
                          live
                            ? undefined
                            : 'Only the latest change can be undone'
                        }
                        disabled={!live}
                        onPress={() => undo(r.batch_id)}
                      />
                    )}
                  </View>
                );
              })
            ) : (
              <CCText style={{fontSize: T.sm + 1, color: E.muted}}>
                No edits to this chapter yet.
              </CCText>
            )}
          </ScrollView>
        </Sheet>
      ) : null}

      {sheet === 'flag' && current ? (
        <Sheet
          title="Flag for later"
          width={480}
          onClose={() => setSheet(null)}>
          <View style={{padding: S[5], gap: S[3]}}>
            <CCText
              nativeID="flagHelp"
              style={{fontSize: T.sm + 1, color: E.body, lineHeight: 20}}>
              Leaves this chapter open for someone to look at. It blocks
              publishing until it is fixed or marked fine.
            </CCText>
            <TextInput
              value={flagNote}
              onChangeText={setFlagNote}
              accessibilityLabel="What should someone check?"
              placeholder="What should someone check?"
              placeholderTextColor={E.muted}
              multiline
              style={{
                minHeight: 72,
                borderWidth: 1,
                borderColor: E.inputLine,
                borderRadius: 10,
                padding: S[3],
                fontSize: T.base,
                color: E.ink,
              }}
            />
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                gap: S[2],
              }}>
              <Button
                label="Cancel"
                variant="quiet"
                onPress={() => setSheet(null)}
              />
              <Button
                label="Flag"
                variant="accent"
                icon="flag"
                onPress={() => {
                  setSheet(null);
                  actions.flag.mutate(
                    {
                      pageId: current.id,
                      kind: 'other',
                      note: flagNote.trim() || 'Flagged for later',
                    },
                    {
                      onError: (e: unknown) =>
                        setBoardNotice(errorText(e, 'Could not flag it.')),
                    },
                  );
                }}
              />
            </View>
          </View>
        </Sheet>
      ) : null}
    </View>
  );
}
